import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { NewHireJourney } from "models/v2/newHireJourney";
import {
  insertSharedResourceInCacheByNewHireJourneyId,
  sharedResourceKeys,
  useSharedResourcesListByNewHireIdQuery,
  useUpdateSharedResourceDateAndTimeByPrefixId,
} from "features/Resources/Shared/hooks";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { actionToCalendarEvent } from "features/Resources/Shared/helpers";
import { DEFAULT_TIMEZONE } from "definitions/constants";
import { Flex } from "@chakra-ui/react";
import TabSelector, { TabOption } from "components/TabSelector/TabSelector";
import useActionSideDrawer from "components/ActionSideDrawer/hooks";
import ActionSideDrawer from "components/ActionSideDrawer";
import { getLocalTimezone } from "helpers/time";
import useConfirmation from "hooks/useConfirmation";
import { titleize } from "helpers/string";
import { CalendarEvent } from "components/Calendar/types";
import { ActionSummary, PrefixId } from "features/Resources/Shared/models";
import { useQueryClient } from "@tanstack/react-query";
import {
  ScheduleWeekView,
  ScheduleMonthView,
  ScheduleListView,
  ScheduleByStages,
} from "features/NewHireJourney/Components/Schedule";

type ViewType = "week" | "month" | "list" | "stages";

interface ScheduleTabProps {
  newHireJourney: NewHireJourney;
}

export default function ScheduleTab({ newHireJourney }: ScheduleTabProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: newHireJourneyId } = useParams<{ id: string }>();

  // Fetch shared resources once
  const { data: actions = [] } = useSharedResourcesListByNewHireIdQuery(newHireJourneyId as string);

  // Timezone (from newHire or local)
  const timezone = newHireJourney?.newHire?.timezone || getLocalTimezone();

  // Parse current view from URL
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const idIndex = pathSegments.findIndex((segment) => segment === newHireJourneyId);
  const currentView = (pathSegments[idIndex + 2] as ViewType) || "list";

  // Parse selectedDate from query param, default to today
  const dateParam = searchParams.get("date");
  const selectedDate = dateParam ? moment(dateParam, "YYYY-MM-DD") : moment();

  // Parse showWeekends from query param
  const showWeekends = searchParams.get("weekends") === "true";

  // Convert actions to calendar events
  const events = useMemo(
    () => actions.map((action) => actionToCalendarEvent(action, timezone ?? DEFAULT_TIMEZONE)),
    [actions, timezone],
  );

  // Shared action drawer state
  const { isOpen, onClose, selectedActionPrefixId, expandClickHandlerByPrefixId } = useActionSideDrawer();
  const { confirm, ConfirmationDialog } = useConfirmation();

  // Event update mutation
  const { mutate: updateActionDateTime } = useUpdateSharedResourceDateAndTimeByPrefixId({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.byNewHireJourneys() });
    },
  });

  // Handlers
  const handleViewChange = (view: ViewType) => {
    // Preserve query params when switching views
    const params = new URLSearchParams(searchParams);
    const queryString = params.toString();
    const path = `/new-hires/${newHireJourneyId}/schedule/${view}`;
    navigate(queryString ? `${path}?${queryString}` : path);
  };

  const handleDateChange = (newDate: moment.Moment) => {
    const params = new URLSearchParams(searchParams);
    params.set("date", newDate.format("YYYY-MM-DD"));
    setSearchParams(params);
  };

  const handleShowWeekendsChange = (value: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("weekends", "true");
    } else {
      params.delete("weekends");
    }
    setSearchParams(params);
  };

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      expandClickHandlerByPrefixId(event.id as PrefixId);
    },
    [expandClickHandlerByPrefixId],
  );

  const handleActionClick = useCallback(
    (action: ActionSummary) => {
      expandClickHandlerByPrefixId(action.prefixId as PrefixId);
    },
    [expandClickHandlerByPrefixId],
  );

  // Event update logic (for drag/drop in calendar)
  const performUpdate = useCallback(
    (action: ActionSummary, newStart: Date, minutes: number) => {
      const result = {
        ...action,
        unifiedDate: newStart,
        action: {
          ...action.action,
          durationMinutes: minutes,
        },
      };
      insertSharedResourceInCacheByNewHireJourneyId(queryClient, newHireJourneyId ?? "", result as ActionSummary);
      updateActionDateTime({
        prefixId: action.prefixId as PrefixId,
        newDate: newStart.toISOString(),
        meetingDuration: minutes,
        action,
      });
    },
    [queryClient, newHireJourneyId, updateActionDateTime],
  );

  const handleEventUpdate = useCallback(
    (id: string, newStart: Date, newEnd: Date) => {
      const action = actions.find((actionSummary) => actionSummary.prefixId === id);
      if (!action) return;

      const newMinutes = moment(newEnd).diff(moment(newStart), "minutes");
      const oldMinutes = action.action?.durationMinutes;
      const timeChanged = !moment.utc(action.unifiedDate).isSame(moment.utc(newStart));
      const durationChanged = action.actionType === "meeting" && oldMinutes !== newMinutes;

      if (!timeChanged && !durationChanged) return;

      const actionName = action.action?.name || `this ${titleize(action.actionType)}`;
      const formattedTime = moment(newStart).tz(timezone).format("dddd, MMMM D [at] h:mm A");

      let changeDescription: string;
      if (timeChanged && durationChanged) {
        changeDescription = `move "${actionName}" to ${formattedTime} and change duration to ${newMinutes} minutes`;
      } else if (timeChanged) {
        changeDescription = `move "${actionName}" to ${formattedTime}`;
      } else {
        changeDescription = `change "${actionName}" duration to ${newMinutes} minutes`;
      }

      confirm({
        header: "Confirm Change",
        body: `Are you sure you want to ${changeDescription}?`,
        confirmButtonText: "Confirm",
        intent: "confirmation",
        onConfirm: () => performUpdate(action, newStart, newMinutes),
      });
    },
    [actions, timezone, confirm, performUpdate],
  );

  return (
    <Flex direction="column" gap="4">
      {/* Top-level tab selector */}
      <Flex justifyContent="flex-end">
        <TabSelector value={currentView} onChange={handleViewChange} size="sm">
          <TabOption value="list">List</TabOption>
          <TabOption value="stages">Stages</TabOption>
          <TabOption value="week">Week</TabOption>
          <TabOption value="month">Month</TabOption>
        </TabSelector>
      </Flex>

      {/* Conditional rendering - delegate to view components */}
      {currentView === "week" && (
        <ScheduleWeekView
          events={events}
          selectedDate={selectedDate}
          timezone={timezone}
          showWeekends={showWeekends}
          onDateChange={handleDateChange}
          onShowWeekendsChange={handleShowWeekendsChange}
          onEventClick={handleEventClick}
          onEventUpdate={handleEventUpdate}
        />
      )}

      {currentView === "month" && (
        <ScheduleMonthView
          events={events}
          selectedDate={selectedDate}
          timezone={timezone}
          showWeekends={showWeekends}
          onDateChange={handleDateChange}
          onShowWeekendsChange={handleShowWeekendsChange}
          onEventClick={handleEventClick}
        />
      )}

      {currentView === "list" && (
        <ScheduleListView
          actions={actions}
          startDate={newHireJourney?.startDate}
          timezone={timezone}
          onActionClick={handleActionClick}
          isComplete={newHireJourney?.status === "archived"}
        />
      )}

      {currentView === "stages" && (
        <ScheduleByStages
          actions={actions}
          newHireStartDate={newHireJourney?.startDate}
          isPending={false}
          onActionClick={handleActionClick}
        />
      )}

      {/* Shared action drawer */}
      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedActionPrefixId} />
      <ConfirmationDialog />
    </Flex>
  );
}
